// The rule model behind the builder's "Who sees this" panel. Converts
// between a stored `display_conditions` condition and an editable rule, picks
// the comparisons each question type offers, and words a rule as a sentence.
// SurveyNavigationService and survey_response_controller evaluate the
// stored shape; keep the operators here in step with them.

import { i18n } from "./i18n"

export interface LogicOption {
  id: number
  label: string
}

export interface LogicQuestion {
  id: number
  title: string
  question_type: string
  section_id: number
  section_title: string
  section_position: number
  options: LogicOption[]
}

export interface StoredCondition {
  question_id: number
  type: "presence" | "blank" | "value"
  operator?: string
  value?: unknown
}

export interface StoredConditions {
  operator: "AND" | "OR"
  conditions: StoredCondition[]
}

// `op` is the comparison as the panel shows it. `answered` and `blank` store
// as `type: presence|blank`; everything else as `type: value` + operator.
// `stored` is the condition the rule was read from, written back as it was
// until the rule is edited, so opening or tidying the panel never rewrites it.
// `unreadable` marks a stored rule the panel can't show as saved (its question
// or answers are gone, or its comparison isn't offered): it still applies.
export interface Rule {
  questionId: number | null
  op: string
  values: string[]
  value: string
  value2: string
  amount: string
  unit: string
  stored?: StoredCondition
  unreadable?: boolean
}

export const RELATIVE_UNITS = ["days", "weeks", "months", "years"]
export const RATING_VALUES = Array.from({ length: 10 }, (_, i) => String(i + 1))
// Choice lists longer than this get a searchable list instead of chips.
export const CHIP_LIMIT = 8

// How a question type's answer control looks.
export type AnswerKind = "chips" | "checkbox" | "number" | "rating" | "date" | "datetime" | "text" | "file" | "signature"

export function answerKind(type: string): AnswerKind | null {
  switch (type) {
    case "boolean":
    case "radio":
    case "select":
    case "likert_scale":
      return "chips"
    case "checkbox":
      return "checkbox"
    case "number":
      return "number"
    case "rating_scale":
      return "rating"
    case "date":
      return "date"
    case "datetime":
      return "datetime"
    case "file_upload":
      return "file"
    case "signature":
      return "signature"
    case "instructions":
      return null
    default:
      return "text"
  }
}

const OPS: Record<AnswerKind, string[]> = {
  chips: ["any_of", "none_of", "answered", "blank"],
  checkbox: ["any_of", "all_of", "none_of", "answered", "blank"],
  number: ["equals", "not_equals", "greater_than", "less_than", "between", "answered", "blank"],
  rating: ["equals", "at_least", "at_most"],
  date: ["less_than", "greater_than", "between", "more_than_ago", "within_last", "answered", "blank"],
  datetime: ["less_than", "greater_than", "answered", "blank"],
  text: ["answered", "blank", "contains", "equals"],
  file: ["answered", "blank"],
  signature: ["answered", "blank"],
}

export function opsFor(question: LogicQuestion): string[] {
  const kind = answerKind(question.question_type)
  return kind ? OPS[kind] : []
}

// The answers a chip rule offers: a boolean's Yes / No, else its options.
export interface Choice {
  id: number | string
  label: string
}

export function choicesFor(question: LogicQuestion): Choice[] {
  if (question.question_type === "boolean") {
    return [
      { id: "true", label: i18n.t("display_logic.yes") },
      { id: "false", label: i18n.t("display_logic.no") },
    ]
  }
  return question.options
}

export function blankRule(question: LogicQuestion | null = null): Rule {
  return {
    questionId: question?.id ?? null,
    op: question ? opsFor(question)[0] : "any_of",
    values: [],
    value: "",
    value2: "",
    amount: "",
    unit: "months",
  }
}

export function ruleFromCondition(condition: StoredCondition, question: LogicQuestion | undefined): Rule {
  const rule = parseCondition(condition, question)
  const readable =
    question !== undefined && offersOp(question, rule.op) && editedCondition(rule) !== null && !dropsAnswers(condition, question)
  return { ...rule, stored: condition, unreadable: !readable }
}

// A stored choice rule naming an answer the question no longer has.
function dropsAnswers(condition: StoredCondition, question: LogicQuestion): boolean {
  const kind = answerKind(question.question_type)
  if (condition.type !== "value" || (kind !== "chips" && kind !== "checkbox")) return false
  const live = new Set(choicesFor(question).map((choice) => String(choice.id)))
  return [condition.value].flat().some((value) => value == null || !live.has(String(value)))
}

function parseCondition(condition: StoredCondition, question: LogicQuestion | undefined): Rule {
  const rule = { ...blankRule(question ?? null), questionId: condition.question_id }
  if (condition.type === "presence") return { ...rule, op: "answered" }
  if (condition.type === "blank") return { ...rule, op: "blank" }

  const operator = condition.operator || "equals"
  const kind = question ? answerKind(question.question_type) : null
  if (kind === "chips" || kind === "checkbox") return choiceRule(rule, operator, condition.value, question!)
  return valueRule(rule, operator, condition.value)
}

// Rules saved before the chip UI hold one option per rule, as equals or
// not_equals. Answers the question no longer has are dropped: no chip shows
// them.
function choiceRule(rule: Rule, operator: string, value: unknown, question: LogicQuestion): Rule {
  const live = new Set(choicesFor(question).map((choice) => String(choice.id)))
  const values = [value].flat().filter((v) => v != null).map(String).filter((id) => live.has(id))
  if (question.question_type === "boolean" && operator === "not_equals") {
    return { ...rule, op: "any_of", values: values.map((v) => (v === "true" ? "false" : "true")) }
  }
  if (operator === "equals") return { ...rule, op: "any_of", values }
  if (operator === "not_equals") return { ...rule, op: "none_of", values }
  return { ...rule, op: operator, values }
}

function valueRule(rule: Rule, operator: string, value: unknown): Rule {
  if (operator === "between" && Array.isArray(value)) {
    return { ...rule, op: operator, value: String(value[0] ?? ""), value2: String(value[1] ?? "") }
  }
  if ((operator === "more_than_ago" || operator === "within_last") && value && typeof value === "object") {
    const relative = value as { amount?: unknown; unit?: unknown }
    return { ...rule, op: operator, amount: String(relative.amount ?? ""), unit: String(relative.unit ?? "months") }
  }
  return { ...rule, op: operator, value: value == null ? "" : String(value) }
}

// The stored condition, or null while the rule is still missing a part.
export function conditionFromRule(rule: Rule): StoredCondition | null {
  return rule.stored ?? editedCondition(rule)
}

// A comparison the question's type offers. Rules from an older builder, or on
// a question retyped since, can hold one it doesn't.
export function offersOp(question: LogicQuestion, op: string): boolean {
  return opsFor(question).includes(op)
}

function editedCondition(rule: Rule): StoredCondition | null {
  if (rule.questionId == null) return null
  const question_id = rule.questionId
  switch (rule.op) {
    case "answered":
      return { question_id, type: "presence" }
    case "blank":
      return { question_id, type: "blank" }
    case "any_of":
    case "all_of":
    case "none_of":
      return rule.values.length ? { question_id, type: "value", operator: rule.op, value: rule.values } : null
    case "between":
      return rule.value && rule.value2 ? { question_id, type: "value", operator: "between", value: [rule.value, rule.value2] } : null
    case "more_than_ago":
    case "within_last": {
      // Whole units only: both runtimes truncate, so 0.5 years would mean today.
      const amount = Math.trunc(Number(rule.amount))
      return amount >= 1 ? { question_id, type: "value", operator: rule.op, value: { amount, unit: rule.unit } } : null
    }
    default:
      return rule.value.trim() ? { question_id, type: "value", operator: rule.op, value: rule.value.trim() } : null
  }
}

export function opLabel(question: LogicQuestion, op: string): string {
  const kind = answerKind(question.question_type)
  return i18n.t(`display_logic.ops.${kind}.${op}`)
}

// In the order the answers are listed, not the order they were picked.
function labelsFor(question: LogicQuestion, ids: string[]): string[] {
  return choicesFor(question)
    .filter((choice) => ids.includes(String(choice.id)))
    .map((choice) => choice.label)
}

// "Mar 15, 2026" rather than "2026-03-15". Read as a calendar date, so no
// time zone shifts it a day.
function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(document.documentElement.lang || undefined, { month: "short", day: "numeric", year: "numeric" })
}

// "“Do you have pets?” is Yes or No"
export function describeRule(rule: Rule, question: LogicQuestion | undefined): string | null {
  if (rule.stored && rule.unreadable) return i18n.t("display_logic.unreadable_rule")
  const condition = editedCondition(rule)
  if (!question || !offersOp(question, rule.op) || !condition) return null

  const title = i18n.t("display_logic.quoted", { title: question.title })
  const kind = answerKind(question.question_type)
  const answer = answerWords(rule, question, condition)
  return i18n.t(`display_logic.sentences.${kind}.${rule.op}`, { question: title, answer }).trim()
}

// The answer part of a rule's sentence: "Dog and Cat", "Mar 15, 2026", "12 months".
function answerWords(rule: Rule, question: LogicQuestion, condition: StoredCondition): string {
  const kind = answerKind(question.question_type)
  const value = (raw: string) => (kind === "date" || kind === "datetime" ? formatDate(raw) : raw)

  switch (rule.op) {
    case "any_of":
    case "all_of":
    case "none_of": {
      const joiner = ` ${i18n.t(rule.op === "all_of" ? "display_logic.and" : "display_logic.or")} `
      return labelsFor(question, rule.values).join(joiner)
    }
    case "between":
      return i18n.t("display_logic.range", { from: value(rule.value), to: value(rule.value2) })
    case "more_than_ago":
    case "within_last":
      return `${(condition.value as { amount: number }).amount} ${i18n.t(`display_logic.units.${rule.unit}`)}`
    case "answered":
    case "blank":
      return ""
    default:
      return value(rule.value)
  }
}
